import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { NIL } from 'uuid';

import theme from '../assets/theme';
import { OrganizationType } from '../hooks/useAccessBadge';
import useAccessBadge from '../hooks/useAccessBadge';
import BackendService, {
  ConfirmationState,
} from '../services/backendService/backendService';
import { isLaunchedInPWA } from '../utils/pwaUtils';
import { Button } from './_atoms/Button';
import BaseModal from './_molecules/BaseModal';
import OrganizationSelector, {
  SelectedLocationType,
  SelectedOrganizationType,
} from './_molecules/OrganizationSelector/OrganizationSelector';

const CountrySchoolSelectionPopupV2 = () => {
  const [isModalVisible, setIsModalVisible] = useState(true);
  const [isUpdatingSchoolInfo, setIsUpdatingSchoolInfo] = useState(false);

  const { accessBadge, setAssignmentConfirmation, refetchAccessBadge } = useAccessBadge();

  // Organization selector state
  const [
    selectedOrganization,
    setSelectedOrganization,
  ] = useState<SelectedOrganizationType | null>(null);
  const [organizationType, setOrganizationType] = useState<OrganizationType | null>(null);
  const [selectedLocation, setSelectedLocation] = useState<SelectedLocationType | null>(
    null,
  );
  const [customOrganizationName, setCustomOrganizationName] = useState<string>();
  const [needsCustomOrganizationName, setNeedsCustomOrganizationName] = useState<boolean>(
    false,
  );
  const [locationFetchError, setLocationFetchError] = useState<string | undefined>();
  const [hideOrganizationTypePicker, setHideOrganizationTypePicker] = useState<boolean>(
    false,
  );

  // Effect to handle access badge values
  useEffect(() => {
    if (accessBadge?.user?.profileInfo?.role) {
      // Set organization type based on role
      const role = accessBadge.user.profileInfo.role;
      switch (role) {
        case 'Teacher':
        case 'School Administrator':
          setOrganizationType(OrganizationType.School);
          setHideOrganizationTypePicker(true);
          break;
        case 'District Administrator':
          setOrganizationType(OrganizationType.District);
          setHideOrganizationTypePicker(true);
          break;
        default:
          setOrganizationType(null);
          setHideOrganizationTypePicker(false);
          break;
      }
    }
  }, [accessBadge]);

  const handleChangeOrgType = useCallback(
    (newOrgType: OrganizationType | null) => {
      if (organizationType && organizationType !== newOrgType) {
        setSelectedOrganization(null);
        setCustomOrganizationName(undefined);
        setNeedsCustomOrganizationName(false);
      }
      setOrganizationType(newOrgType);
    },
    [organizationType, setNeedsCustomOrganizationName],
  );

  const handleChangeLocation = (location: SelectedLocationType | null) => {
    if (
      selectedLocation?.placeAutocomplete?.place_id !==
      location?.placeAutocomplete?.place_id
    ) {
      setSelectedOrganization(null);
      setCustomOrganizationName(undefined);
      setNeedsCustomOrganizationName(false);
    }
    setSelectedLocation(location);
  };

  const handleChangeOrganization = (organization: SelectedOrganizationType | null) => {
    setSelectedOrganization(organization);
  };

  const handleRemindMeLater = async () => {
    setAssignmentConfirmation({
      confirmationState: ConfirmationState.ConfirmedForDuration,
      onSuccessFunction: () => {
        refetchAccessBadge();
      },
    });
    setIsModalVisible(false);
  };

  const handleSubmit = useCallback(async () => {
    if (!accessBadge) return;
    setIsUpdatingSchoolInfo(true);
    try {
      if (selectedOrganization) {
        // This could all be done in a single call to reselectOrganization,
        // but we're breaking it up by scenario for clarity
        const newOrgId =
          !selectedOrganization.organization.id ||
          selectedOrganization.organization.id === NIL
            ? undefined
            : selectedOrganization.organization.id;
        const newTenant = newOrgId
          ? await BackendService.instance.organizations.reselectOrganization({
              newOrgId,
            })
          : await BackendService.instance.organizations.reselectOrganization({
              customOrgName: customOrganizationName,
              city: selectedLocation?.place?.city,
              region: selectedLocation?.place?.region,
              country: selectedLocation?.place?.country,
              primaryZipCode: selectedLocation?.place?.zipCode,
              organizationType: organizationType ?? undefined,
            });

        setIsModalVisible(false);

        // If we're using a custom organization name, we need to refetch access badge
        // to refresh the cache
        if (!newOrgId) {
          await refetchAccessBadge();
          location.reload();
        } else {
          // Can't use switchTenant here because we it requires access to this current tenant
          if (isLaunchedInPWA()) {
            window.location.replace(`${window.location.origin}/${newTenant.id}`);
            return;
          }
          window.open(`${window.location.origin}/${newTenant.id}`, '_self');
        }
      }
    } finally {
      setIsUpdatingSchoolInfo(false);
    }
  }, [
    accessBadge,
    selectedOrganization,
    customOrganizationName,
    selectedLocation?.place?.city,
    selectedLocation?.place?.region,
    selectedLocation?.place?.country,
    selectedLocation?.place?.zipCode,
    organizationType,
    refetchAccessBadge,
  ]);

  const shouldShowNextButtonAfterOrgSelection = () => {
    if (selectedOrganization) {
      return true;
    }

    if (needsCustomOrganizationName && customOrganizationName) {
      return true;
    }

    const isInternationalOrIncomplete =
      selectedLocation?.place?.country && selectedLocation?.place?.country !== 'US';
    if (isInternationalOrIncomplete && customOrganizationName) {
      return true;
    }

    return false;
  };

  return (
    <BaseModal
      title={`Complete your profile`}
      visible={isModalVisible}
      maskClosable={false}
    >
      <FormContainer>
        <OrganizationSelector
          organizationType={organizationType}
          onSelectOrganizationType={handleChangeOrgType}
          onSelectLocation={handleChangeLocation}
          selectedLocation={selectedLocation}
          onSelectOrganization={handleChangeOrganization}
          selectedOrganization={selectedOrganization}
          customOrganizationName={customOrganizationName}
          onSetCustomOrganizationName={setCustomOrganizationName}
          locationFetchError={locationFetchError}
          onLocationFetchError={setLocationFetchError}
          needsCustomOrganizationName={needsCustomOrganizationName}
          onSetNeedsCustomOrganizationName={setNeedsCustomOrganizationName}
          hideOrganizationTypePicker={hideOrganizationTypePicker}
          fluid
          metricEventNamePrefix="CountrySchoolPopup"
          disableSearchRetry={false}
        />

        {shouldShowNextButtonAfterOrgSelection() && (
          <Button
            onClick={handleSubmit}
            styles={{ marginTop: 20 }}
            disabled={isUpdatingSchoolInfo}
          >
            Continue
          </Button>
        )}
        <RemindLaterLink onClick={handleRemindMeLater}>Remind me later</RemindLaterLink>
      </FormContainer>
    </BaseModal>
  );
};

export default CountrySchoolSelectionPopupV2;

const FormContainer = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  justify-content: center;
`;

const RemindLaterLink = styled.a`
  color: ${theme.colors.text.darkGrey};
  text-decoration: underline;
  cursor: pointer;
  margin-top: 10px;
  &:hover {
    color: ${theme.colors.text.medium};
  }
`;
